import React, { useMemo, useState, useCallback } from 'react';
import { FilterButton, Form, SimpleList } from '@app/components';
import { timesheetApi, userApi } from '@app/store';
import { ITimesheet, IUser, IShop } from '@app/common';
import { createColumnHelper } from '@tanstack/react-table';
import { useTable } from '@app/hooks/useTable';
import { DateTime } from 'luxon';
import { DateTimePicker } from '@app/components/form/DateTimePicker.web';
import { row } from '@app/style';
import { normalize } from '@app/helpers';
import styled from '@2pulse/responsive';
import { useForm } from 'react-hook-form';
import { useAppSelector } from '@app/store/hooks';
import { UserSelect } from '@app/components/form/selects/features/UserSelect';
import { View, Text } from 'react-native';
import { theme } from '@app/theme';

const columnHelper = createColumnHelper<ITimesheet>();

// Fonction pour convertir un buffer MongoDB en string ObjectId
const bufferToObjectId = (value: any): string | null => {
  if (!value) return null;
  
  // Si c'est déjà une string, la retourner
  if (typeof value === 'string') return value;
  
  // Si c'est un objet avec un buffer
  if (value?.buffer?.data && Array.isArray(value.buffer.data)) {
    return value.buffer.data
      .map((byte: number) => byte.toString(16).padStart(2, '0'))
      .join('');
  }
  
  // Si c'est un objet avec _id
  if (value?._id) {
    return bufferToObjectId(value._id);
  }
  
  return null;
};

const getStartOfDayISO = (date: string | null) => {
  return date ? DateTime.fromISO(date).startOf('day').toISO() : null;
};

const getEndOfDayISO = (date: string | null) => {
  return date ? DateTime.fromISO(date).endOf('day').toISO() : null;
};

// Types de période
type PeriodType = 'week' | 'month' | 'year' | 'custom';

// Helpers pour les périodes
const getWeekRange = (refDate: DateTime = DateTime.now()) => ({
  fromDate: refDate.startOf('week').toISO(),
  toDate: refDate.endOf('week').toISO(),
});

const getMonthRange = (refDate: DateTime = DateTime.now()) => ({
  fromDate: refDate.startOf('month').toISO(),
  toDate: refDate.endOf('month').toISO(),
});

const getYearRange = (refDate: DateTime = DateTime.now()) => ({
  fromDate: refDate.startOf('year').toISO(),
  toDate: refDate.endOf('year').toISO(),
});

// Fonction pour obtenir le label de la période
const getPeriodLabel = (periodType: PeriodType, refDate: DateTime): string => {
  switch (periodType) {
    case 'week':
      return `Semaine ${refDate.weekNumber} - ${refDate.year}`;
    case 'month':
      return refDate.setLocale('fr').toFormat('MMMM yyyy');
    case 'year':
      return refDate.year.toString();
    default:
      return 'Personnalisé';
  }
};

const startOfTodayISO = DateTime.now().endOf('day').toISO();
const oneMonthAgoISO = DateTime.now().minus({ months: 1 }).startOf('day').toISO();

interface TimesheetFilters {
  fromDate: string | null;
  toDate: string | null;
  users: IUser[];
}

interface TimesheetStats {
  totalHours: number;
  totalMinutes: number;
  totalRecords: number;
  averageHoursPerDay: number;
  uniqueEmployees: number;
}

const calculateStats = (timesheets: ITimesheet[]): TimesheetStats => {
  if (!timesheets || timesheets.length === 0) {
    return {
      totalHours: 0,
      totalMinutes: 0,
      totalRecords: 0,
      averageHoursPerDay: 0,
      uniqueEmployees: 0,
    };
  }

  let totalMs = 0;
  const uniqueDays = new Set<string>();
  const uniqueUsers = new Set<string>();

  timesheets.forEach((ts) => {
    const clockingIn = new Date(ts.clockingIn);
    const clockingOut = new Date(ts.clockingOut);
    totalMs += clockingOut.getTime() - clockingIn.getTime();
    
    uniqueDays.add(DateTime.fromJSDate(clockingIn).toFormat('yyyy-MM-dd'));
    const userId = bufferToObjectId(ts.userId);
    if (userId) uniqueUsers.add(userId);
  });

  const totalHours = Math.floor(totalMs / (1000 * 60 * 60));
  const totalMinutes = Math.floor((totalMs % (1000 * 60 * 60)) / (1000 * 60));
  const averageHoursPerDay = uniqueDays.size > 0 ? totalMs / (1000 * 60 * 60) / uniqueDays.size : 0;

  return {
    totalHours,
    totalMinutes,
    totalRecords: timesheets.length,
    averageHoursPerDay: Math.round(averageHoursPerDay * 100) / 100,
    uniqueEmployees: uniqueUsers.size,
  };
};

export const TimesheetList = () => {
  const [readMany, { isFetching: isExportingCSV }] = timesheetApi.useLazyReadManyTimesheetsQuery();
  const [generatePdf, { isLoading: isExportingPDF }] = timesheetApi.useGeneratePdfMutation();
  
  // État pour le type de période et la date de référence
  const [periodType, setPeriodType] = useState<PeriodType>('month');
  const [referenceDate, setReferenceDate] = useState<DateTime>(DateTime.now());
  
  // Récupérer tous les utilisateurs pour faire la jointure côté frontend
  const { data: usersData } = userApi.useReadManyUsersQuery({
    pagination: { skip: 0, take: 1000 },
  });

  // Créer un map des utilisateurs par ID pour un accès rapide
  const usersMap = useMemo(() => {
    const map = new Map<string, IUser>();
    if (usersData?.data) {
      usersData.data.forEach((user) => {
        map.set(user._id!, user);
      });
    }
    return map;
  }, [usersData]);

  // Fonction pour récupérer le nom d'un utilisateur
  const getUserName = useCallback((userId: string | IUser | any): string => {
    if (!userId) return 'N/A';
    
    // Si c'est déjà un objet utilisateur avec firstname/lastname
    if (typeof userId === 'object' && userId.firstname && userId.lastname) {
      return `${userId.firstname} ${userId.lastname}`;
    }
    
    // Convertir le buffer en ObjectId string si nécessaire
    const id = bufferToObjectId(userId);
    if (id && usersMap.has(id)) {
      const user = usersMap.get(id)!;
      return `${user.firstname} ${user.lastname}`;
    }
    
    return 'N/A';
  }, [usersMap]);

  const columns = [
    columnHelper.display({
      id: 'user',
      header: () => (
        <SimpleList.HeaderItem width="20%">
          <SimpleList.HeaderItemLabel>Employé</SimpleList.HeaderItemLabel>
        </SimpleList.HeaderItem>
      ),
      cell: ({ row, active }) => {
        const userName = getUserName(row.original.userId);
        
        return (
          <SimpleList.ItemCol width="20%">
            <SimpleList.ItemImageWrapper>
              <SimpleList.ItemText align="left" bold numberOfLines={2} active={active}>
                {userName}
              </SimpleList.ItemText>
            </SimpleList.ItemImageWrapper>
          </SimpleList.ItemCol>
        );
      },
    }),
    columnHelper.display({
      id: 'date',
      header: () => (
        <SimpleList.HeaderItem width="15%">
          <SimpleList.HeaderItemLabel>Date</SimpleList.HeaderItemLabel>
        </SimpleList.HeaderItem>
      ),
      cell: ({ row, active }) => {
        return (
          <SimpleList.ItemCol width="15%">
            <SimpleList.ItemImageWrapper>
              <SimpleList.ItemText align="left" numberOfLines={2} active={active}>
                {DateTime.fromISO(row.original.clockingIn as any).toFormat('dd/MM/yyyy')}
              </SimpleList.ItemText>
            </SimpleList.ItemImageWrapper>
          </SimpleList.ItemCol>
        );
      },
    }),
    columnHelper.accessor('clockingIn', {
      header: () => (
        <SimpleList.HeaderItem width="15%">
          <SimpleList.HeaderItemLabel>Arrivée</SimpleList.HeaderItemLabel>
        </SimpleList.HeaderItem>
      ),
      cell: (info) => {
        return (
          <SimpleList.ItemCol width="15%">
            <SimpleList.ItemImageWrapper>
              <SimpleList.ItemText align="left" numberOfLines={2} active={info.active}>
                {DateTime.fromISO(info.getValue()).toFormat('HH:mm')}
              </SimpleList.ItemText>
            </SimpleList.ItemImageWrapper>
          </SimpleList.ItemCol>
        );
      },
    }),
    columnHelper.accessor('clockingOut', {
      header: () => (
        <SimpleList.HeaderItem width="15%">
          <SimpleList.HeaderItemLabel>Départ</SimpleList.HeaderItemLabel>
        </SimpleList.HeaderItem>
      ),
      cell: (info) => {
        return (
          <SimpleList.ItemCol width="15%">
            <SimpleList.ItemImageWrapper>
              <SimpleList.ItemText align="left" numberOfLines={2} active={info.active}>
                {DateTime.fromISO(info.getValue()).toFormat('HH:mm')}
              </SimpleList.ItemText>
            </SimpleList.ItemImageWrapper>
          </SimpleList.ItemCol>
        );
      },
    }),
    columnHelper.display({
      id: 'duration',
      header: () => (
        <SimpleList.HeaderItem width="15%">
          <SimpleList.HeaderItemLabel>Durée</SimpleList.HeaderItemLabel>
        </SimpleList.HeaderItem>
      ),
      cell: ({ row, active }) => {
        const clockingIn = new Date(row.original.clockingIn);
        const clockingOut = new Date(row.original.clockingOut);
        const durationMs = clockingOut.getTime() - clockingIn.getTime();
        const hours = Math.floor(durationMs / (1000 * 60 * 60));
        const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
        
        return (
          <SimpleList.ItemCol width="15%">
            <SimpleList.ItemImageWrapper>
              <SimpleList.ItemText align="left" numberOfLines={2} active={active}>
                {`${hours}h ${minutes.toString().padStart(2, '0')}min`}
              </SimpleList.ItemText>
            </SimpleList.ItemImageWrapper>
          </SimpleList.ItemCol>
        );
      },
    }),
  ];

  const formMethods = useForm<TimesheetFilters>({
    defaultValues: {
      fromDate: oneMonthAgoISO,
      toDate: startOfTodayISO,
      users: [],
    }
  });

  const selectedShop = useAppSelector((state) => state.app.selectedShop);

  const [fromDate, toDate, selectedUsers] = formMethods.watch(['fromDate', 'toDate', 'users']);

  const fromDateISO = getStartOfDayISO(fromDate);
  const toDateISO = getEndOfDayISO(toDate);

  const buildFilters = useCallback(() => {
    const filters: any = {
      $and: [
        ...(!!fromDateISO ? [{
          clockingIn: {
            $gte: fromDateISO
          }
        }] : []),
        ...(!!toDateISO ? [{
          clockingOut: {
            $lte: toDateISO
          }
        }] : []),
        ...(selectedUsers && selectedUsers.length > 0 ? [{
          userId: {
            $in: selectedUsers.map((u: IUser) => u._id)
          }
        }] : []),
        ...(!!selectedShop ? [{
          shopId: {
            $eq: selectedShop._id
          }
        }] : []),
      ]
    };
    return filters.$and.length > 0 ? filters : {};
  }, [fromDateISO, toDateISO, selectedUsers, selectedShop]);

  const { table, setSearch, isFetching, data } = useTable({
    columns,
    useQuery: ({ pagination }) => timesheetApi.useReadManyTimesheetsQuery({
      pagination,
      filters: buildFilters(),
      sort: ['clockingIn:DESC'],
    })
  });

  // Calculate stats from current data
  const stats = useMemo(() => {
    return calculateStats(data?.data || []);
  }, [data]);

  // Fonction pour appliquer une période
  const applyPeriod = useCallback((type: PeriodType, refDate: DateTime) => {
    let range: { fromDate: string | null; toDate: string | null };
    
    switch (type) {
      case 'week':
        range = getWeekRange(refDate);
        break;
      case 'month':
        range = getMonthRange(refDate);
        break;
      case 'year':
        range = getYearRange(refDate);
        break;
      default:
        return;
    }
    
    formMethods.setValue('fromDate', range.fromDate);
    formMethods.setValue('toDate', range.toDate);
  }, [formMethods]);

  // Sélection du type de période
  const selectPeriodType = useCallback((type: PeriodType) => {
    setPeriodType(type);
    const now = DateTime.now();
    setReferenceDate(now);
    applyPeriod(type, now);
  }, [applyPeriod]);

  // Navigation précédent/suivant
  const navigatePeriod = useCallback((direction: 'prev' | 'next') => {
    const offset = direction === 'prev' ? -1 : 1;
    let newRefDate: DateTime;
    
    switch (periodType) {
      case 'week':
        newRefDate = referenceDate.plus({ weeks: offset });
        break;
      case 'month':
        newRefDate = referenceDate.plus({ months: offset });
        break;
      case 'year':
        newRefDate = referenceDate.plus({ years: offset });
        break;
      default:
        return;
    }
    
    setReferenceDate(newRefDate);
    applyPeriod(periodType, newRefDate);
  }, [periodType, referenceDate, applyPeriod]);

  // Aller à aujourd'hui
  const goToToday = useCallback(() => {
    const now = DateTime.now();
    setReferenceDate(now);
    applyPeriod(periodType, now);
  }, [periodType, applyPeriod]);

  // Label de la période actuelle
  const periodLabel = useMemo(() => {
    if (periodType === 'custom') return 'Personnalisé';
    return getPeriodLabel(periodType, referenceDate);
  }, [periodType, referenceDate]);

  // Fonction pour récupérer l'utilisateur complet
  const getUser = useCallback((userId: string | IUser | any): IUser | null => {
    if (!userId) return null;
    
    if (typeof userId === 'object' && userId.firstname && userId.lastname) {
      return userId as IUser;
    }
    
    // Convertir le buffer en ObjectId string si nécessaire
    const id = bufferToObjectId(userId);
    if (id && usersMap.has(id)) {
      return usersMap.get(id)!;
    }
    
    return null;
  }, [usersMap]);

  // Export functions
  const exportCSV = async () => {
    try {
      const result = await readMany({
        pagination: { skip: 0, take: 10000 },
        filters: buildFilters(),
        sort: ['clockingIn:DESC'],
      }).unwrap();

      if (!result?.data) {
        console.error('No data to export');
        return;
      }

      const csvHeaders = [
        'ID',
        'Employé',
        'Email',
        'Date',
        'Heure d\'arrivée',
        'Heure de départ',
        'Durée (heures)',
        'Durée (minutes)',
      ];

      const csvRows = result.data.map((ts: ITimesheet) => {
        const user = getUser(ts.userId);
        const userName = user ? `${user.firstname} ${user.lastname}` : 'N/A';
        const userEmail = user?.email || 'N/A';

        const clockingIn = DateTime.fromISO(ts.clockingIn as any);
        const clockingOut = DateTime.fromISO(ts.clockingOut as any);
        const durationMs = clockingOut.toMillis() - clockingIn.toMillis();
        const hours = Math.floor(durationMs / (1000 * 60 * 60));
        const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));

        return [
          ts._id,
          userName,
          userEmail,
          clockingIn.toFormat('dd/MM/yyyy'),
          clockingIn.toFormat('HH:mm'),
          clockingOut.toFormat('HH:mm'),
          hours.toString(),
          minutes.toString(),
        ];
      });

      const periodLabel = `${DateTime.fromISO(fromDateISO!).toFormat('dd-MM-yyyy')}_${DateTime.fromISO(toDateISO!).toFormat('dd-MM-yyyy')}`;

      const csvContent = [
        csvHeaders.join(';'),
        ...csvRows.map(row => row.join(';'))
      ].join('\n');

      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `pointages_${periodLabel}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Failed to export CSV:', error);
    }
  };

  const exportPDF = async () => {
    if (isExportDisabled) return;

    try {
      // Construire le usersMap pour l'envoyer au backend
      const usersMapForPdf: Record<string, { firstname: string; lastname: string }> = {};
      usersMap.forEach((user, id) => {
        usersMapForPdf[id] = { firstname: user.firstname, lastname: user.lastname };
      });

      await generatePdf({
        startDate: fromDateISO!,
        endDate: toDateISO!,
        userIds: selectedUsers?.length > 0 ? selectedUsers.map((u: IUser) => u._id) : undefined,
        usersMap: usersMapForPdf,
      }).unwrap();
    } catch (error) {
      console.error('Failed to export PDF:', error);
    }
  };

  const isExportDisabled = !fromDateISO || !toDateISO;

  return (
    <SimpleList
      table={table}
      isFetching={isFetching}
      onSearch={setSearch}
      fullWidth
      SearchComponent={(
        <Form formMethods={formMethods}>
          <FiltersContainer>
            <FilterRow>
              <UserSelect
                name="users"
                label="Filtrer par employés"
                onWhite
                isMultiple
              />

              <DateTimePicker
                name="fromDate"
                type="date"
                label="Date de début"
                onWhite
              />

              <DateTimePicker
                name="toDate"
                type="date"
                label="Date de fin"
                onWhite
              />
            </FilterRow>

            <ActionsRow>
              <PeriodSelectorContainer>
                <PeriodTypeButtons>
                  <PeriodTypeButton 
                    active={periodType === 'week'} 
                    onPress={() => selectPeriodType('week')}
                  >
                    <PeriodTypeButtonText active={periodType === 'week'}>Semaine</PeriodTypeButtonText>
                  </PeriodTypeButton>
                  <PeriodTypeButton 
                    active={periodType === 'month'} 
                    onPress={() => selectPeriodType('month')}
                  >
                    <PeriodTypeButtonText active={periodType === 'month'}>Mois</PeriodTypeButtonText>
                  </PeriodTypeButton>
                  <PeriodTypeButton 
                    active={periodType === 'year'} 
                    onPress={() => selectPeriodType('year')}
                  >
                    <PeriodTypeButtonText active={periodType === 'year'}>Année</PeriodTypeButtonText>
                  </PeriodTypeButton>
                </PeriodTypeButtons>

                <PeriodNavigation>
                  <NavButton onPress={() => navigatePeriod('prev')}>
                    <NavButtonText>◀</NavButtonText>
                  </NavButton>
                  <PeriodLabelContainer onPress={goToToday}>
                    <PeriodLabelText>{periodLabel}</PeriodLabelText>
                  </PeriodLabelContainer>
                  <NavButton onPress={() => navigatePeriod('next')}>
                    <NavButtonText>▶</NavButtonText>
                  </NavButton>
                </PeriodNavigation>
              </PeriodSelectorContainer>

              <ExportContainer>
                <FilterButton
                  title="Export CSV"
                  disabled={isExportDisabled}
                  loading={isExportingCSV}
                  onPress={exportCSV}
                />
                <FilterButton
                  title="Export PDF"
                  disabled={isExportDisabled}
                  loading={isExportingPDF}
                  onPress={exportPDF}
                />
              </ExportContainer>
            </ActionsRow>

            <StatsContainer>
              <StatCard>
                <StatValue>{stats.totalRecords}</StatValue>
                <StatLabel>Pointages</StatLabel>
              </StatCard>
              <StatCard>
                <StatValue>{stats.totalHours}h {stats.totalMinutes.toString().padStart(2, '0')}min</StatValue>
                <StatLabel>Total heures</StatLabel>
              </StatCard>
              <StatCard>
                <StatValue>{stats.averageHoursPerDay}h</StatValue>
                <StatLabel>Moyenne/jour</StatLabel>
              </StatCard>
              <StatCard>
                <StatValue>{stats.uniqueEmployees}</StatValue>
                <StatLabel>Employés</StatLabel>
              </StatCard>
            </StatsContainer>
          </FiltersContainer>
        </Form>
      )}
    />
  );
};

const FiltersContainer = styled.View({
  width: '100%',
  paddingLeft: 106,
  paddingRight: normalize(21),
  paddingBottom: normalize(8),
  paddingTop: normalize(8),
});

const FilterRow = styled.View({
  ...row,
  columnGap: normalize(13),
  rowGap: normalize(8),
  flexWrap: 'wrap',
  alignItems: 'center',
  marginBottom: normalize(12),
});

const ActionsRow = styled.View({
  ...row,
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: normalize(16),
  flexWrap: 'wrap',
  rowGap: normalize(12),
});

const PeriodSelectorContainer = styled.View({
  ...row,
  alignItems: 'center',
  columnGap: normalize(16),
  flexWrap: 'wrap',
  rowGap: normalize(8),
});

const PeriodTypeButtons = styled.View({
  ...row,
  backgroundColor: '#E5E7EB',
  borderRadius: normalize(8),
  padding: normalize(4),
});

const PeriodTypeButton = styled.TouchableOpacity<{ active?: boolean }>(({ theme, props }) => ({
  paddingVertical: normalize(8),
  paddingHorizontal: normalize(16),
  borderRadius: normalize(6),
  backgroundColor: props.active ? theme.colors.celadonblue : 'transparent',
}));

const PeriodTypeButtonText = styled.Text<{ active?: boolean }>(({ theme, props }) => ({
  fontSize: normalize(13),
  fontWeight: '600',
  color: props.active ? '#FFF' : theme.colors.oxfordblue,
}));

const PeriodNavigation = styled.View({
  ...row,
  alignItems: 'center',
  columnGap: normalize(8),
});

const NavButton = styled.TouchableOpacity(({ theme }) => ({
  width: normalize(36),
  height: normalize(36),
  borderRadius: normalize(18),
  backgroundColor: theme.colors.celadonblue + '15',
  justifyContent: 'center',
  alignItems: 'center',
}));

const NavButtonText = styled.Text(({ theme }) => ({
  fontSize: normalize(14),
  color: theme.colors.celadonblue,
  fontWeight: '600',
}));

const PeriodLabelContainer = styled.TouchableOpacity(({ theme }) => ({
  paddingVertical: normalize(8),
  paddingHorizontal: normalize(16),
  backgroundColor: theme.colors.oxfordblue + '08',
  borderRadius: normalize(8),
  minWidth: normalize(180),
  alignItems: 'center',
}));

const PeriodLabelText = styled.Text(({ theme }) => ({
  fontSize: normalize(14),
  fontWeight: '600',
  color: theme.colors.oxfordblue,
  textTransform: 'capitalize',
}));

const ExportContainer = styled.View({
  ...row,
  columnGap: normalize(8),
});

const StatsContainer = styled.View({
  ...row,
  columnGap: normalize(16),
  flexWrap: 'wrap',
  rowGap: normalize(8),
  paddingTop: normalize(12),
  borderTopWidth: 1,
  borderTopColor: '#E5E7EB',
});

const StatCard = styled.View(({ theme }) => ({
  backgroundColor: theme.colors.celadonblue + '10',
  borderRadius: normalize(8),
  paddingVertical: normalize(10),
  paddingHorizontal: normalize(16),
  minWidth: normalize(120),
  alignItems: 'center',
}));

const StatValue = styled.Text(({ theme }) => ({
  fontSize: normalize(18),
  fontWeight: '700',
  color: theme.colors.celadonblue,
}));

const StatLabel = styled.Text(({ theme }) => ({
  fontSize: normalize(11),
  fontWeight: '500',
  color: theme.colors.oxfordblue,
  opacity: 0.7,
  marginTop: normalize(2),
}));
